// Development-only chat data used to exercise the existing chat UI locally.
const LOCAL_CHAT_UI_FIXTURE_HOSTNAMES = Object.freeze(['localhost', '127.0.0.1', '::1']);

const normalizeBrowserHostname = (hostname) => (hostname === '[::1]' ? '::1' : hostname);

export const isLocalChatUiFixtureEnabled = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.VUE_APP_MODE === 'development' &&
  typeof window !== 'undefined' &&
  LOCAL_CHAT_UI_FIXTURE_HOSTNAMES.includes(normalizeBrowserHostname(window.location.hostname));

export const LOCAL_CHAT_UI_FIXTURE_IMAGE_PATH = require('@/assets/static/images/gimchi.png');
export const LOCAL_CHAT_UI_FIXTURE_ROOM_NOS = Object.freeze([92001, 92002, 92003, 92004, 92005]);
export const LOCAL_CHAT_UI_FIXTURE_ORDER_NOS = Object.freeze([72001, 72004, 72005]);

const LOCAL_CHAT_UI_FIXTURE_ORDER_ROOM_MAP = Object.freeze({
  72001: 92001,
  72004: 92004,
  72005: 92005
});

// LOCAL UI FIXTURE: retained only for the lifetime of this loaded JavaScript module.
const readRoomNos = new Set();
const orderStateOverrides = new Map();

export const isLocalChatUiFixtureRoomNo = (roomNo) =>
  LOCAL_CHAT_UI_FIXTURE_ROOM_NOS.includes(Number(roomNo));

export const isLocalChatUiFixtureOrderNo = (orderNo) =>
  LOCAL_CHAT_UI_FIXTURE_ORDER_NOS.includes(Number(orderNo));

export const getLocalChatUiFixtureRoomNoByOrderNo = (orderNo) =>
  LOCAL_CHAT_UI_FIXTURE_ORDER_ROOM_MAP[Number(orderNo)] ?? null;

export const markLocalChatUiFixtureRoomRead = (roomNo) => {
  const numericRoomNo = Number(roomNo);
  if (!isLocalChatUiFixtureRoomNo(numericRoomNo)) return false;

  readRoomNos.add(numericRoomNo);
  return true;
};

export const updateLocalChatUiFixtureOrderState = (orderNo, orderState) => {
  const numericOrderNo = Number(orderNo);
  if (!isLocalChatUiFixtureOrderNo(numericOrderNo) || !orderState) return false;

  orderStateOverrides.set(numericOrderNo, orderState);
  return true;
};

export const createLocalChatUiProfile = (userNo, name, fixtureSender) => ({
  userNo,
  name,
  fixtureSender,
  profile: {
    storedFileName: ''
  }
});

export const createLocalChatUiMessage = ({
  bubbleNo,
  roomNo,
  userNo,
  userName,
  fixtureSender,
  content,
  regDate,
  type = 'PLAIN',
  file = null,
  isRead = true
}) => ({
  bubbleNo,
  roomNo,
  userNo,
  userName,
  fixtureSender,
  content,
  type,
  file,
  regDate,
  isRead
});

export const createLocalChatUiStore = ({ storeNo, userNo, nameKr, nameVn, nameEn = nameVn }) => ({
  storeNo,
  userNo,
  nameKr,
  nameVn,
  nameEn,
  phone: '0901234567',
  isChattable: true,
  isQueryable: true,
  isNegoDeliveryFee: false,
  businessStatus: 'OPEN'
});

export const createLocalChatUiOrder = ({
  orderNo,
  orderManageNo,
  orderType,
  orderState,
  orderDate,
  userNo,
  name,
  inquiry,
  reservedDate = null
}) => ({
  orderNo,
  orderManageNo,
  orderType,
  orderState,
  orderDate,
  reservedDate,
  userNo,
  name,
  inquiry,
  address: 'Local fixture address',
  addressDetail: 'UI preview only',
  phone: '0909876543',
  latitude: 10.7769,
  longitude: 106.7009,
  paymentMethod: 'CARD',
  bank: '',
  accountNumber: '',
  depositHolder: '',
  balance: 245000,
  deliveryFee: 15000,
  details: [
    {
      nameKr: 'Fixture menu',
      nameVn: 'Fixture menu',
      nameEn: 'Fixture menu',
      stock: 2,
      price: 115000,
      discountRate: 0,
      optionPrice: 0,
      options: [
        {
          optionNameKr: 'Local option',
          optionNameVn: 'Local option',
          optionNameEn: 'Local option',
          additionalFee: 0
        }
      ]
    }
  ]
});

export const createLocalChatUiRoom = ({ roomNo, roomType, store, profiles, chats, order = {}, unreadCount }) => ({
  roomNo,
  roomType,
  orderNo: order.orderNo ?? null,
  store,
  profiles,
  order,
  isChattable: true,
  isActive: true,
  lastBubble: { ...chats[chats.length - 1] },
  ...(typeof unreadCount === 'number' ? { unreadCount } : {})
});

const TERMINAL_ORDER_STATES = new Set([
  'ORDER_CANCELLED',
  'DELIVERY_COMPLETED',
  'PACKAGING_COMPLETED',
  'PICKUP_COMPLETED'
]);

const isProgressedFixtureOrder = (room) =>
  room.roomType === 'ORDER' &&
  room.order?.orderState !== 'ORDER_INCOME' &&
  !TERMINAL_ORDER_STATES.has(room.order?.orderState);

const sumUnreadCounts = (rooms) =>
  rooms.reduce((total, room) => total + Number(room?.unreadCount ?? 0), 0);

const createLocalChatUiBubbleCounts = (rooms) => {
  const orderRooms = rooms.filter((room) => room.roomType === 'ORDER');
  const inquiryRooms = rooms.filter((room) => room.roomType === 'INQUIRY');
  const orderBubbleCount = sumUnreadCounts(orderRooms);
  const inquiryBubbleCount = sumUnreadCounts(inquiryRooms);

  return {
    incomingOrderBubbleCount: sumUnreadCounts(
      orderRooms.filter((room) => room.order?.orderState === 'ORDER_INCOME')
    ),
    progressedOrderBubbleCount: sumUnreadCounts(orderRooms.filter(isProgressedFixtureOrder)),
    reserveOrderBubbleCount: sumUnreadCounts(
      orderRooms.filter((room) => room.order?.orderType === 'TYPE_VISIT')
    ),
    orderBubbleCount,
    inquiryBubbleCount,
    totalBubbleCount: orderBubbleCount + inquiryBubbleCount
  };
};

export const createLocalSentMessage = ({
  roomNo,
  currentUser = {},
  userNo = currentUser?.userNo,
  userName = currentUser?.name,
  content = '',
  file = null,
  chats = [],
  bubbleNo,
  regDate = new Date().toISOString()
} = {}) => {
  const numericUserNo = Number(userNo ?? 0);
  const nextBubbleNo =
    bubbleNo ?? Math.max(0, ...chats.map((message) => Number(message?.bubbleNo ?? 0))) + 1;

  return createLocalChatUiMessage({
    bubbleNo: nextBubbleNo,
    roomNo: Number(roomNo ?? 0),
    userNo: numericUserNo,
    userName: userName || 'Local UI User',
    fixtureSender: 'OWN',
    content,
    type: file ? 'IMAGE' : 'PLAIN',
    file,
    regDate,
    isRead: true
  });
};

export const createLocalChatUiFixture = (currentUser = {}) => {
  const ownUserNo = Number(currentUser?.userNo ?? 0);
  const ownName = currentUser?.name || 'Local UI User';
  const isSeller = currentUser?.isSeller === true;
  const counterpartUserNos = Array.from({ length: 5 }, (_, index) => ownUserNo + 9200001 + index);
  const storeUserNo = (index) => (isSeller ? ownUserNo : counterpartUserNos[index]);
  const orderUserNo = (index) => (isSeller ? counterpartUserNos[index] : ownUserNo);
  const ownProfile = createLocalChatUiProfile(ownUserNo, ownName, 'OWN');

  const stores = [
    createLocalChatUiStore({
      storeNo: 82001,
      userNo: storeUserNo(0),
      nameKr: 'Mini Mart',
      nameVn: 'Mini Mart'
    }),
    createLocalChatUiStore({
      storeNo: 82002,
      userNo: storeUserNo(1),
      nameKr: 'A Very Long Local Fixture Store Name for Wrapping and Overflow Checks',
      nameVn: 'A Very Long Local Fixture Store Name for Wrapping and Overflow Checks'
    }),
    createLocalChatUiStore({
      storeNo: 82003,
      userNo: storeUserNo(2),
      nameKr: 'Bowl',
      nameVn: 'Bowl'
    }),
    createLocalChatUiStore({
      storeNo: 82004,
      userNo: storeUserNo(3),
      nameKr: 'Saigon Fixture Kitchen',
      nameVn: 'Saigon Fixture Kitchen'
    }),
    createLocalChatUiStore({
      storeNo: 82005,
      userNo: storeUserNo(4),
      nameKr: 'Tea',
      nameVn: 'Tea'
    })
  ];

  const counterpartNames = ['Minh', 'Lan', 'Hana', 'Bao', 'An'];
  const profiles = stores.map((_, index) => [
    { ...ownProfile },
    createLocalChatUiProfile(counterpartUserNos[index], counterpartNames[index], 'OTHER')
  ]);

  const chatsByRoom = {
    92001: [
      createLocalChatUiMessage({
        bubbleNo: 9200101,
        roomNo: 92001,
        userNo: counterpartUserNos[0],
        userName: counterpartNames[0],
        fixtureSender: 'OTHER',
        content:
          'This deliberately long plain message checks wrapping, maximum bubble width, line spacing, and readability while the same counterpart sends more than one message in a row.',
        regDate: '2026-08-24T09:12:00+07:00',
        isRead: true
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200102,
        roomNo: 92001,
        userNo: counterpartUserNos[0],
        userName: counterpartNames[0],
        fixtureSender: 'OTHER',
        content: 'Got it.',
        regDate: '2026-08-24T09:13:00+07:00',
        isRead: false
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200103,
        roomNo: 92001,
        userNo: ownUserNo,
        userName: ownName,
        fixtureSender: 'OWN',
        content:
          'Here is another long plain message, this time from the current user, so consecutive own-message alignment and wrapping can be reviewed together.',
        regDate: '2026-08-24T09:15:00+07:00',
        isRead: false
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200104,
        roomNo: 92001,
        userNo: ownUserNo,
        userName: ownName,
        fixtureSender: 'OWN',
        content: 'Thanks!',
        regDate: '2026-08-24T09:16:00+07:00',
        isRead: true
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200105,
        roomNo: 92001,
        userNo: counterpartUserNos[0],
        userName: counterpartNames[0],
        fixtureSender: 'OTHER',
        content: 'Fixture image',
        type: 'IMAGE',
        file: {
          originalFileName: 'gimchi.png',
          storedFileName: '',
          fixtureStaticPath: LOCAL_CHAT_UI_FIXTURE_IMAGE_PATH
        },
        regDate: '2026-08-24T09:18:00+07:00',
        isRead: false
      })
    ],
    92002: [
      createLocalChatUiMessage({
        bubbleNo: 9200201,
        roomNo: 92002,
        userNo: ownUserNo,
        userName: ownName,
        fixtureSender: 'OWN',
        content: 'Do you have a vegetarian option?',
        regDate: '2026-08-24T08:35:00+07:00'
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200202,
        roomNo: 92002,
        userNo: counterpartUserNos[1],
        userName: counterpartNames[1],
        fixtureSender: 'OTHER',
        content:
          'Yes. This deliberately long last message verifies lobby clamping and overflow behavior while the extra-long store name occupies the title area.',
        regDate: '2026-08-24T08:42:00+07:00'
      })
    ],
    92003: [
      createLocalChatUiMessage({
        bubbleNo: 9200301,
        roomNo: 92003,
        userNo: counterpartUserNos[2],
        userName: counterpartNames[2],
        fixtureSender: 'OTHER',
        content: 'Yes.',
        regDate: '2026-08-23T18:03:00+07:00',
        isRead: false
      })
    ],
    92004: [
      createLocalChatUiMessage({
        bubbleNo: 9200401,
        roomNo: 92004,
        userNo: ownUserNo,
        userName: ownName,
        fixtureSender: 'OWN',
        content: 'Please confirm the delivery time.',
        regDate: '2026-08-22T14:21:00+07:00'
      }),
      createLocalChatUiMessage({
        bubbleNo: 9200402,
        roomNo: 92004,
        userNo: counterpartUserNos[3],
        userName: counterpartNames[3],
        fixtureSender: 'OTHER',
        content: 'Delivery is scheduled for 7 PM.',
        regDate: '2026-08-22T14:24:00+07:00'
      })
    ],
    92005: [
      createLocalChatUiMessage({
        bubbleNo: 9200501,
        roomNo: 92005,
        userNo: counterpartUserNos[4],
        userName: counterpartNames[4],
        fixtureSender: 'OTHER',
        content: 'Your reserved table is ready.',
        regDate: '2026-08-21T11:05:00+07:00',
        isRead: false
      })
    ]
  };

  const rooms = [
    createLocalChatUiRoom({
      roomNo: 92001,
      roomType: 'ORDER',
      store: stores[0],
      profiles: profiles[0],
      chats: chatsByRoom[92001],
      unreadCount: 2,
      order: createLocalChatUiOrder({
        orderNo: 72001,
        orderManageNo: 'LOCAL-ORDER-92001',
        orderType: 'TYPE_DELIVERY',
        orderState: orderStateOverrides.get(72001) ?? 'ORDER_CHECK',
        orderDate: '2026-08-24T08:50:00+07:00',
        userNo: orderUserNo(0),
        name: isSeller ? counterpartNames[0] : ownName,
        inquiry: 'Please leave the order at the door.'
      })
    }),
    createLocalChatUiRoom({
      roomNo: 92002,
      roomType: 'INQUIRY',
      store: stores[1],
      profiles: profiles[1],
      chats: chatsByRoom[92002]
    }),
    createLocalChatUiRoom({
      roomNo: 92003,
      roomType: 'INQUIRY',
      store: stores[2],
      profiles: profiles[2],
      chats: chatsByRoom[92003],
      unreadCount: 1
    }),
    createLocalChatUiRoom({
      roomNo: 92004,
      roomType: 'ORDER',
      store: stores[3],
      profiles: profiles[3],
      chats: chatsByRoom[92004],
      order: createLocalChatUiOrder({
        orderNo: 72004,
        orderManageNo: 'LOCAL-ORDER-92004',
        orderType: 'TYPE_DELIVERY',
        orderState: orderStateOverrides.get(72004) ?? 'ORDER_CHECK',
        orderDate: '2026-08-22T13:45:00+07:00',
        userNo: orderUserNo(3),
        name: isSeller ? counterpartNames[3] : ownName,
        inquiry: 'Please call on arrival.'
      })
    }),
    createLocalChatUiRoom({
      roomNo: 92005,
      roomType: 'ORDER',
      store: stores[4],
      profiles: profiles[4],
      chats: chatsByRoom[92005],
      unreadCount: 1,
      order: createLocalChatUiOrder({
        orderNo: 72005,
        orderManageNo: 'LOCAL-ORDER-92005',
        orderType: 'TYPE_VISIT',
        orderState: orderStateOverrides.get(72005) ?? 'DELIVERY_COMPLETED',
        orderDate: '2026-08-21T10:30:00+07:00',
        reservedDate: '2026-08-25T12:00:00+07:00',
        userNo: orderUserNo(4),
        name: isSeller ? counterpartNames[4] : ownName,
        inquiry: 'Window seat if available.'
      })
    })
  ];

  rooms.forEach((room) => {
    const roomChats = chatsByRoom[room.roomNo] ?? [];
    const wasRead = readRoomNos.has(room.roomNo);

    if (wasRead) {
      roomChats.forEach((message) => {
        if (Number(message.userNo) !== ownUserNo) message.isRead = true;
      });
    }

    room.unreadCount = wasRead
      ? 0
      : roomChats.filter((message) => Number(message.userNo) !== ownUserNo && !message.isRead).length;
    room.lastBubble = { ...(roomChats.at(-1) ?? {}) };

    if (room.order?.orderState === 'ORDER_CANCELLED') {
      room.isChattable = false;
      room.store.isChattable = false;
    }
  });

  return {
    rooms,
    chatsByRoom,
    bubbleCounts: createLocalChatUiBubbleCounts(rooms)
  };
};

const createLocalMyOrderDetail = (detail, order, store, index) => {
  const productNo = Number(`${order.orderNo}${index + 1}`);
  const detailsPrice = Number(detail.price ?? 0);
  const discountRate = Number(detail.discountRate ?? 0);
  const optionPrice = Number(detail.optionPrice ?? 0);
  const stock = Number(detail.stock ?? 0);
  const unitPrice = detailsPrice - Number((detailsPrice * discountRate).toFixed(0)) + optionPrice;

  return {
    orderDetailNo: productNo,
    orderNo: order.orderNo,
    productNo,
    stock,
    detailsPrice,
    discountRate,
    optionPrice,
    totalPrice: unitPrice * stock,
    isCancelled: false,
    file: { storedFileName: '' },
    options: (detail.options ?? []).map((option, optionIndex) => ({
      orderDetailOptionNo: Number(`${productNo}${optionIndex + 1}`),
      nameKr: option.optionNameKr,
      nameVn: option.optionNameVn,
      nameEn: option.optionNameEn,
      additionalFee: Number(option.additionalFee ?? 0)
    })),
    product: {
      productNo,
      storeNo: store.storeNo,
      nameKr: detail.nameKr,
      nameVn: detail.nameVn,
      nameEn: detail.nameEn,
      price: detailsPrice,
      discountRate,
      stock: 99,
      isActive: true,
      isShow: true,
      isOrderable: true,
      isManageStock: false,
      optionGroups: [],
      file: { storedFileName: '' }
    }
  };
};

const createLocalOrderStageTimes = (order) => {
  const state = order.orderState;
  const cookingOrLater = new Set([
    'COOKING',
    'DELIVERY',
    'DELIVERY_ARRIVED',
    'DELIVERY_COMPLETED',
    'PACKAGING_COMPLETED',
    'PICKUP_COMPLETED'
  ]);
  const deliveryOrLater = new Set(['DELIVERY', 'DELIVERY_ARRIVED', 'DELIVERY_COMPLETED']);
  const completed = new Set(['DELIVERY_COMPLETED', 'PACKAGING_COMPLETED', 'PICKUP_COMPLETED']);

  return {
    orderIncomeTime: order.orderDate,
    orderCheckTime: state === 'ORDER_INCOME' ? null : order.orderDate,
    cookingTime: cookingOrLater.has(state) ? order.orderDate : null,
    deliveryTime: deliveryOrLater.has(state) ? order.orderDate : null,
    completedTime: completed.has(state) ? order.reservedDate ?? order.orderDate : null,
    cancelledTime: state === 'ORDER_CANCELLED' ? order.orderDate : null
  };
};

export const createLocalMyOrderFixtures = (currentUser = {}) => {
  const { rooms } = createLocalChatUiFixture(currentUser);

  return rooms
    .filter((room) => room.roomType === 'ORDER' && isLocalChatUiFixtureOrderNo(room.orderNo))
    .map((room) => {
      const orderDetails = (room.order.details ?? []).map((detail, index) =>
        createLocalMyOrderDetail(detail, room.order, room.store, index)
      );
      const totalProductsPrice = orderDetails.reduce((total, detail) => total + detail.totalPrice, 0);

      return {
        ...room.order,
        ...createLocalOrderStageTimes(room.order),
        storeNo: room.store.storeNo,
        store: { ...room.store },
        orderDetails,
        totalProductsPrice,
        discountAmount: 0,
        couponDiscountAmount: 0,
        pointUsageAmount: 0,
        cancelledAmount: 0,
        reviewNo: null,
        isShow: true,
        payStatus: 'PAYMENT_COMPLETED',
        regDate: room.order.orderDate,
        modDate: room.order.orderDate
      };
    });
};

export const findLocalChatUiFixtureOrder = (orderNo, currentUser = {}) =>
  createLocalMyOrderFixtures(currentUser).find(
    (order) => Number(order.orderNo) === Number(orderNo)
  ) ?? null;

export const filterLocalChatUiFixtureRooms = (rooms, { roomType = null, orderTab = null } = {}) => {
  const typedRooms = roomType ? rooms.filter((room) => room.roomType === roomType) : [...rooms];

  if (roomType !== 'ORDER' || !orderTab || orderTab === 'ALL') return typedRooms;
  if (orderTab === 'INCOME') {
    return typedRooms.filter((room) => room.order?.orderState === 'ORDER_INCOME');
  }
  if (orderTab === 'RESERVATION') {
    return typedRooms.filter((room) => room.order?.orderType === 'TYPE_VISIT');
  }
  if (orderTab === 'ON_GOING') {
    return typedRooms.filter(isProgressedFixtureOrder);
  }

  return typedRooms;
};

export const findLocalChatUiFixtureRoom = (roomNo, currentUser = {}) => {
  const fixture = createLocalChatUiFixture(currentUser);
  const numericRoomNo = Number(roomNo);

  return {
    ...fixture,
    room: fixture.rooms.find((item) => item.roomNo === numericRoomNo) ?? null,
    chats: fixture.chatsByRoom[numericRoomNo] ?? []
  };
};

export const findLocalChatUiFixtureRoomByOrderNo = (orderNo, currentUser = {}) => {
  const roomNo = getLocalChatUiFixtureRoomNoByOrderNo(orderNo);
  if (!roomNo) return null;

  return findLocalChatUiFixtureRoom(roomNo, currentUser).room;
};
